<template>
    <div>
        <input
            :id="inputId"
            ref="input"
            v-model="model"
            :class="{ 'is-error': error, 'form-field--light': lightColor }"
            :disabled="disabled"
            :autofocus="autofocus"
            :name="name"
            :placeholder="placeholder"
            type="tel"
            maxlength="18"
            class="form-field__input"
            autocomplete="tel"
            :aria-invalid="!!error || undefined"
            :aria-describedby="error ? errorId : undefined"
            @input="onInput"
            @paste="onInput"
            @change="onInput"
            @focus="emit('focus')"
        >
        <span v-if="error" :id="errorId" class="error-message">{{ error }}</span>
    </div>
</template>

<script setup lang="ts">
import {useUniqueId} from '~/composables/useUniqueId';

const props = defineProps({
  label: {type: String},
  placeholder: {type: String, default: ''},
  lightColor: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
  autofocus: {type: Boolean, default: false},
  error: {type: String, default: ''},
  name: {type: String, required: true},
  note: {type: String},
  id: {type: String, default: undefined},
});

const model = defineModel<string>({ default: '' });

const input = ref(null);
const emit = defineEmits(['focus']);

const generatedId = useUniqueId(props.name)
const inputId = computed(() => props.id ?? generatedId)
const errorId = computed(() => `${inputId.value}-error`)

function replaceNumberForInput(value: string) {
    const withoutMaskPrefix = value.replace(/^\+7\s*/, '');
    let number = withoutMaskPrefix.replace(/\D/g, '');
    if(number === '7' || number === '8') {
        number = '';
    } else if(number.length > 10 && (number.startsWith('7') || number.startsWith('8'))) {
        number = number.substring(1);
    }

    number = number.substring(0, 10);

   const zip = number.substring(0, 3);
    const middle = number.substring(3, 6);
    const last = number.substring(6, 8);
    const lastFour = number.substring(8, 10);

    let result = '+7 ';
    if(zip.length && zip.length < 3) {
        result += '(' + zip;
        return result;
    }
    if(zip.length === 3) {
        result += '(' + zip;
    }
    if(middle.length) {
        result += ') ' + middle;
    }
    if(last.length) {
        result += '-' + last;
    }
    if(lastFour.length) {
        result += '-' + lastFour;
    }
    return result
}

function onInput(event: Event) {
    const el = event.target as HTMLInputElement;
    const formatted = replaceNumberForInput(el.value);

    /**
     * Набранный вручную код страны сворачивается в ту же маску («+7 » → «+7 »),
     * и Vue не перерисовывает input, потому что значение модели не изменилось.
     * В DOM тогда остаётся сырой ввод «+7 7», и следующая цифра разбирается
     * вместе с ним — номер на экране уезжает на разряд.
     */
    model.value = formatted;

    if (el.value !== formatted) {
        el.value = formatted;
    }
}

/**
 * Safari (AutoFill из Контактов/Связки ключей, восстановление формы при
 * возврате «Назад») и менеджеры паролей вставляют значение прямо в DOM,
 * не отправляя событие input. Модель vee-validate остаётся пустой, и UI,
 * завязанный на неё (кнопка «Подтвердить», поле кода из SMS), не появляется.
 * Поэтому периодически сверяем DOM-значение с моделью и синхронизируем.
 */
const syncFromDom = () => {
    const el = input.value as HTMLInputElement | null;
    if (!el || el.disabled) return;

    const domValue = el.value;
    if (!domValue || domValue === model.value) return;

    const formatted = replaceNumberForInput(domValue);
    if (formatted !== model.value) {
        model.value = formatted;
    }
    if (el.value !== formatted) {
        el.value = formatted;
    }
};

let syncTimer: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
    syncTimer = setInterval(syncFromDom, 400);
    // Восстановление страницы из bfcache — сразу сверяем, не дожидаясь тика таймера
    window.addEventListener('pageshow', syncFromDom);
});

onBeforeUnmount(() => {
    if (syncTimer) clearInterval(syncTimer);
    window.removeEventListener('pageshow', syncFromDom);
});
</script>
